import { cachedFetch } from './hooks/useCachedFetch';
import type { ApporteurStats, DateType, SouscriptionEntry } from './types';

/**
 * Client API du module KPI consolidés.
 *
 * Spécificités :
 *  - Bearer JWT passé explicitement (l'intercepteur 401 silent refresh
 *    côté `services/api.ts` ne couvre que les requêtes faites via le
 *    wrapper `api.get/post/...`. Ici on utilise `fetch` natif pour
 *    préserver le cache 20 min et la parallélisation N/N-1).
 *  - Si le backend renvoie 401 (token expiré), on échoue en clair — la
 *    prochaine navigation utilisera le pattern d'auth CRM (redirect login).
 *    Hypothèse : la page KPI consolidés reste ouverte peu de temps ;
 *    un rafraîchissement au bout de 15 min n'est pas bloquant.
 */

export interface SouscriptionsListParams {
  company?: string[];
  dateFrom?: string;
  dateTo?: string;
  dateType: DateType;
}

function toQueryParams(p: SouscriptionsListParams): Record<string, string | string[] | undefined> {
  // Cache client key = queryString qui sert aussi de params envoyés au
  // backend. N'ajouter AUCUNE clé interne (`_t` etc.) ici : le schema Zod
  // backend est `.strict()` et rejette toute clé non reconnue.
  //
  // Isolation inter-users : le cache est un Map module-level partagé
  // entre tous les mounts. À la déconnexion, `clearClientCache()` peut
  // être appelé depuis `stores/auth.ts::clear`. Risque réel en V1 : si
  // un admin se déconnecte et un autre se reconnecte dans la même page
  // sans reload, il verrait le cache de l'ancien jusqu'à expiration
  // (20 min max) — scénario très rare sur un CRM interne.
  return {
    company: p.company,
    dateFrom: p.dateFrom,
    dateTo: p.dateTo,
    dateType: p.dateType,
  };
}

export const kpiConsolidesApi = {
  souscriptions: (accessToken: string, params: SouscriptionsListParams) =>
    cachedFetch<SouscriptionEntry[]>('/api/v1/souscriptions', toQueryParams(params), {
      headers: { Authorization: `Bearer ${accessToken}` },
    }),
  apporteurs: (accessToken: string, params: SouscriptionsListParams) =>
    cachedFetch<ApporteurStats>('/api/v1/souscriptions/apporteurs', toQueryParams(params), {
      headers: { Authorization: `Bearer ${accessToken}` },
    }),
};
